import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { lintGutter } from '@codemirror/lint';
import { githubDark, githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import { memo, useCallback, useEffect, useRef } from 'react';

import ErrorOutline from '@mui/icons-material/ErrorOutline';
import ReportProblemOutlined from '@mui/icons-material/ReportProblemOutlined';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import {
  ScaffolderFieldError,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '../../../components';
import { useYamlLinter } from '../hooks/useYamlLinter';
import { GENERATORS_INFO } from '../infoDoc';
import { argoCDGeneratorSchema } from '../schema/generatorsSchema';
import { useGeneratorsStyles, useStyles } from '../styles';
import { generatorsInputErrors } from '../validation';

export interface ApplicationSetGeneratorsSectionProps {
  value: string;
  onChange: (value: string) => void;
  errors: string[];
}

const ApplicationSetGeneratorsSectionInner = ({
  value,
  onChange,
  errors,
}: ApplicationSetGeneratorsSectionProps) => {
  const { classes } = useStyles();
  const { classes: generatorClasses } = useGeneratorsStyles();
  const theme = useTheme();
  const sectionRef = useRef<HTMLDivElement>(null);
  const generatorsError = errors.length > 0 ? generatorsInputErrors(value) : '';
  const {
    yamlLinter,
    errors: yamlErrors,
    warnings: yamlWarnings,
  } = useYamlLinter(argoCDGeneratorSchema);
  const errorCount = yamlErrors?.length ?? 0;
  const warningCount = yamlWarnings?.length ?? 0;

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (generatorsError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [generatorsError]);

  const handleChange = useCallback((v: string) => {
    onChangeRef.current(v);
  }, []);

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderInputHeader
          title="Generators"
          description="Define generators to automatically create multiple ArgoCD Applications from this ApplicationSet."
          variant="h5"
          divider
          infoDoc={GENERATORS_INFO}
        />
        <Box margin={1}>
          <Box className={generatorClasses.codeMirror} sx={{ height: 300 }}>
            <CodeMirror
              value={value || ''}
              extensions={[yamlLang(), lintGutter(), yamlLinter]}
              onChange={handleChange}
              theme={theme.palette.mode === 'dark' ? githubDark : githubLight}
              placeholder="Enter ArgoCD ApplicationSet generator YAML"
            />
          </Box>
          <Box className={generatorClasses.validationContainer}>
            <Box
              className={`${generatorClasses.validationCount} ${
                errorCount > 0
                  ? generatorClasses.validationErrorStyle
                  : generatorClasses.validationDefaultStyle
              }`}
              aria-label="error-count"
              title={`${errorCount} error${errorCount === 1 ? '' : 's'}`}
            >
              <ErrorOutline fontSize="small" />
              <span>{errorCount}</span>
            </Box>
            <Box
              className={`${generatorClasses.validationCount} ${
                warningCount > 0
                  ? generatorClasses.validationWarningStyle
                  : generatorClasses.validationDefaultStyle
              }`}
              aria-label="warning-count"
              title={`${warningCount} warning${warningCount === 1 ? '' : 's'}`}
            >
              <ReportProblemOutlined fontSize="small" />
              <span>{warningCount}</span>
            </Box>
          </Box>
          {generatorsError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={generatorsError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const ApplicationSetGeneratorsSection = memo(
  ApplicationSetGeneratorsSectionInner,
);
